import { runHttpPing } from './probes/http-ping.js';
import { measureCapacityDownload, measureCapacityUpload, measureDownload, measureUpload } from './probes/bandwidth.js';
import { runBufferbloat } from './probes/bufferbloat.js';
import { runWebsocketNotifier } from './probes/websocket.js';
import { runWebrtcProbe } from './probes/webrtc.js';
import { evaluateAll, overallResult } from './scoring.js';
import { captureEnvironment } from './env.js';
import { collectMachineFitness } from './machine.js';

const DOWNLOAD_BYTES = 50 * 1024 * 1024;
const UPLOAD_BYTES = 10 * 1024 * 1024;
const CAPACITY_DOWNLOAD_BYTES_PER_STREAM = 50 * 1024 * 1024;
const CAPACITY_UPLOAD_BYTES_PER_STREAM = 2 * 1024 * 1024;
const CAPACITY_STREAMS = 4;
const CAPACITY_DOWNLOAD_URL = 'https://speed.cloudflare.com/__down';
const CAPACITY_UPLOAD_URL = 'https://speed.cloudflare.com/__up';
const PHASE_TIMEOUT_GRACE_MS = 10_000;
const BANDWIDTH_PROGRESS_INTERVAL_MS = 500;

export function buildSchedule({
  machineMs = 45_000,
  idleBaselineMs = 30_000,
  bandwidthMs = 90_000,
  bufferbloatMs = 30_000,
  webrtcMs = 240_000
}) {
  const machine = { startMs: 0, endMs: machineMs };
  const idle = { startMs: machine.endMs, endMs: machine.endMs + idleBaselineMs };
  const bandwidth = { startMs: idle.endMs, endMs: idle.endMs + bandwidthMs };
  const bufferbloat = { startMs: bandwidth.endMs, endMs: bandwidth.endMs + bufferbloatMs };
  const webrtc = { startMs: bufferbloat.endMs, endMs: bufferbloat.endMs + webrtcMs };
  const websocket = { startMs: machine.endMs, endMs: webrtc.endMs };
  return {
    totalMs: webrtc.endMs,
    phases: { machine, idle, bandwidth, bufferbloat, webrtc, websocket }
  };
}

function phaseDurationMs(schedule, phase) {
  return schedule.phases[phase].endMs - schedule.phases[phase].startMs;
}

export async function runWithTimeBudget({ label, timeoutMs, run, fallback }) {
  let timeoutId;
  const timeout = new Promise(resolve => {
    timeoutId = setTimeout(() => {
      const error = new Error(`${label} exceeded ${timeoutMs}ms time budget`);
      resolve(fallback(error));
    }, timeoutMs);
  });

  try {
    return await Promise.race([run(), timeout]);
  } finally {
    clearTimeout(timeoutId);
  }
}

function finiteNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function deriveNetworkMetrics({ webrtc, bufferbloat, ws, bandwidth = {} }) {
  const webrtcCompleted = webrtc.completed === true;
  const bufferbloatMeasured = bufferbloat.increaseMs !== null && bufferbloat.increaseMs !== undefined;
  const websocketMeasured = (ws.count || 0) > 0;
  const download = bandwidth.download || {};
  const upload = bandwidth.upload || {};
  const capacityDownload = bandwidth.capacityDownload || {};
  const capacityUpload = bandwidth.capacityUpload || {};

  return {
    downloadMbps: finiteNumberOrNull(download.mbps),
    uploadMbps: finiteNumberOrNull(upload.mbps),
    capacityDownloadMbps: finiteNumberOrNull(capacityDownload.mbps),
    capacityUploadMbps: finiteNumberOrNull(capacityUpload.mbps),
    capacityDownloadCompleted: capacityDownload.completed === true,
    capacityUploadCompleted: capacityUpload.completed === true,
    downloadTimedOut: download.timedOut === true,
    uploadTimedOut: upload.timedOut === true,
    packetLossPct: webrtcCompleted ? webrtc.packetLossPct : null,
    jitterMs: webrtcCompleted ? webrtc.jitterMs : null,
    rttMs: webrtcCompleted ? webrtc.rttMs : null,
    mosScore: webrtcCompleted ? undefined : null,
    bufferbloatIncreaseMs: bufferbloatMeasured ? bufferbloat.increaseMs : null,
    websocketP95Ms: websocketMeasured ? ws.p95Ms : null,
    websocketReconnects: websocketMeasured ? ws.reconnects : null
  };
}

export function networkReliabilityResult(verdicts) {
  return overallResult({
    packetLossPct: verdicts.packetLossPct,
    jitterMs: verdicts.jitterMs,
    rttMs: verdicts.rttMs,
    bufferbloatIncreaseMs: verdicts.bufferbloatIncreaseMs,
    websocketP95Ms: verdicts.websocketP95Ms,
    websocketReconnects: verdicts.websocketReconnects,
    mosScore: verdicts.mosScore,
    capacityDownloadMbps: verdicts.capacityDownloadMbps,
    capacityUploadMbps: verdicts.capacityUploadMbps
  });
}

export async function runAllProbes({
  serverHttpUrl,
  serverWsUrl,
  schedule = buildSchedule({}),
  machineProbe = collectMachineFitness,
  onPhaseChange = () => {},
  onProgress = () => {}
}) {
  const startedAt = new Date().toISOString();

  onPhaseChange({ phase: 'machine-check', schedule });
  const machineDurationMs = phaseDurationMs(schedule, 'machine');
  const machine = await runWithTimeBudget({
    label: 'machine check',
    timeoutMs: machineDurationMs + PHASE_TIMEOUT_GRACE_MS,
    run: () => machineProbe({
      durationMs: machineDurationMs,
      onProgress: progress => onProgress({ probe: 'machine', durationMs: machineDurationMs, ...progress })
    }),
    fallback: error => ({
      error: error.message,
      cpuOpsPerSec: null,
      cpuVerdict: 'no_data',
      deviceMemoryGb: null,
      cpuCores: null,
      browserVersion: null,
      browserAgeMonths: null,
      hardwareAccelerationEnabled: null,
      webrtcSupported: null,
      webcodecsSupported: null,
      webgl2Supported: null,
      hwVideoCodecs: null,
      hwAudioCodecs: null,
      incognitoDetected: null,
      onBattery: null,
      memoryHeadroomBytes: null,
      clutterScore: null,
      machineVerdict: 'inconclusive',
      verdicts: {},
      raw: { error: error.message }
    })
  });

  // WebSocket runs for the network-probe duration.
  onPhaseChange({ phase: 'websocket-start', schedule });
  const websocketDurationMs = schedule.phases.websocket.endMs - schedule.phases.websocket.startMs;
  const wsPromise = runWebsocketNotifier({
    wsUrl: `${serverWsUrl}/ws/notifier`,
    durationMs: websocketDurationMs,
    onSample: rtt => onProgress({ probe: 'websocket', rtt })
  });

  // Phase 1: idle baseline ping (separate from bufferbloat's own idle phase).
  onPhaseChange({ phase: 'idle' });
  const idleDurationMs = phaseDurationMs(schedule, 'idle');
  const idleStartedAt = Date.now();
  const idlePings = await runWithTimeBudget({
    label: 'idle latency',
    timeoutMs: idleDurationMs + PHASE_TIMEOUT_GRACE_MS,
    run: () => runHttpPing({
      serverUrl: serverHttpUrl,
      durationMs: idleDurationMs,
      intervalMs: 500,
      onSample: rtt => onProgress({ probe: 'http-ping', rtt, elapsedMs: Date.now() - idleStartedAt, durationMs: idleDurationMs })
    }),
    fallback: error => ({
      error: error.message,
      samples: [],
      count: 0,
      errors: 0,
      minMs: 0,
      maxMs: 0,
      meanMs: 0,
      medianMs: 0,
      p50Ms: 0,
      p95Ms: 0
    })
  });

  // Phase 2: bandwidth.
  onPhaseChange({ phase: 'bandwidth' });
  const bandwidthDurationMs = phaseDurationMs(schedule, 'bandwidth');
  const downloadSampleMs = Math.max(1_000, Math.min(10_000, Math.floor(bandwidthDurationMs * 0.15)));
  const uploadTimeoutMs = Math.max(1_000, Math.min(12_000, Math.floor(bandwidthDurationMs * 0.15)));
  const capacityDownloadSampleMs = Math.max(2_000, Math.min(15_000, Math.floor(bandwidthDurationMs * 0.25)));
  const capacityUploadTimeoutMs = Math.max(2_000, Math.min(15_000, Math.floor(bandwidthDurationMs * 0.20)));
  const dl = await measureDownload({
    serverUrl: serverHttpUrl,
    bytes: DOWNLOAD_BYTES,
    timeoutMs: downloadSampleMs,
    sampleDurationMs: downloadSampleMs,
    progressIntervalMs: BANDWIDTH_PROGRESS_INTERVAL_MS,
    onProgress: bytes => onProgress({ probe: 'bandwidth', stage: 'single-download', bytes, totalBytes: DOWNLOAD_BYTES })
  }).catch(err => ({ error: String(err), bytes: 0, durationMs: 0, mbps: 0 }));
  const up = await measureUpload({
    serverUrl: serverHttpUrl,
    bytes: UPLOAD_BYTES,
    timeoutMs: uploadTimeoutMs,
    onProgress: progress => onProgress({ probe: 'bandwidth', stage: 'single-upload', ...progress })
  }).catch(err => ({ error: String(err), bytes: 0, durationMs: 0, mbps: 0 }));
  const capacityDownload = await measureCapacityDownload({
    serverUrl: serverHttpUrl,
    downloadUrl: CAPACITY_DOWNLOAD_URL,
    provider: 'cloudflare-edge',
    streams: CAPACITY_STREAMS,
    bytesPerStream: CAPACITY_DOWNLOAD_BYTES_PER_STREAM,
    timeoutMs: capacityDownloadSampleMs,
    sampleDurationMs: capacityDownloadSampleMs,
    progressIntervalMs: BANDWIDTH_PROGRESS_INTERVAL_MS,
    onProgress: progress => onProgress({ probe: 'bandwidth', stage: 'capacity-download', ...progress })
  }).catch(err => ({ error: String(err), bytes: 0, durationMs: 0, mbps: 0, completed: false }));
  const capacityUpload = await measureCapacityUpload({
    serverUrl: serverHttpUrl,
    uploadUrl: CAPACITY_UPLOAD_URL,
    expectUploadJson: false,
    provider: 'cloudflare-edge',
    streams: CAPACITY_STREAMS,
    bytesPerStream: CAPACITY_UPLOAD_BYTES_PER_STREAM,
    timeoutMs: capacityUploadTimeoutMs,
    onProgress: progress => onProgress({ probe: 'bandwidth', stage: 'capacity-upload', ...progress })
  }).catch(err => ({ error: String(err), bytes: 0, durationMs: 0, mbps: 0, completed: false }));

  // Phase 3: bufferbloat (saturating, alone besides ws).
  onPhaseChange({ phase: 'bufferbloat' });
  const bufferbloatDurationMs = phaseDurationMs(schedule, 'bufferbloat');
  const bufferbloat = await runWithTimeBudget({
    label: 'bufferbloat',
    timeoutMs: bufferbloatDurationMs + PHASE_TIMEOUT_GRACE_MS,
    run: () => runBufferbloat({
      serverUrl: serverHttpUrl,
      idleDurationMs: 5_000, // short intra-bufferbloat idle since we already did 30s of idle pings above
      loadedDurationMs: bufferbloatDurationMs - 5_000,
      pingIntervalMs: 500,
      downloadBytes: DOWNLOAD_BYTES,
      uploadBytes: UPLOAD_BYTES,
      onPhase: subphase => onProgress({ probe: 'bufferbloat', subphase, durationMs: bufferbloatDurationMs })
    }),
    fallback: error => ({
      error: error.message,
      idle: { samples: [], count: 0, errors: 0 },
      loadedPings: [],
      download: { error: error.message, bytes: 0, durationMs: 0, mbps: 0, completed: false },
      upload: { error: error.message, bytes: 0, durationMs: 0, mbps: 0, completed: false },
      idleP50Ms: 0,
      loadedP95Ms: 0,
      increaseMs: null
    })
  });

  // Phase 4: WebRTC long-haul.
  onPhaseChange({ phase: 'webrtc' });
  const webrtcDurationMs = phaseDurationMs(schedule, 'webrtc');
  const packetsPerSecond = 50;
  const webrtcFallback = err => ({
    error: err?.message || String(err),
    sent: 0, received: 0,
    completed: false,
    packetLossPct: null, jitterMs: null,
    rttMs: null, rttP50Ms: null, rttP95Ms: null, rttMinMs: null, rttMaxMs: null,
    rttSamples: []
  });
  const webrtc = await runWithTimeBudget({
    label: 'WebRTC voice-quality probe',
    timeoutMs: webrtcDurationMs + PHASE_TIMEOUT_GRACE_MS,
    run: () => runWebrtcProbe({
      signalingUrl: `${serverWsUrl}/ws/webrtc`,
      durationMs: webrtcDurationMs,
      packetsPerSecond,
      onProgress: stats => onProgress({ probe: 'webrtc', durationMs: webrtcDurationMs, expectedPackets: Math.floor((webrtcDurationMs / 1000) * packetsPerSecond), ...stats })
    }).catch(webrtcFallback),
    fallback: webrtcFallback
  });

  // Wait for WebSocket phase to finish.
  const ws = await runWithTimeBudget({
    label: 'WebSocket notifier probe',
    timeoutMs: websocketDurationMs + PHASE_TIMEOUT_GRACE_MS,
    run: () => wsPromise,
    fallback: error => ({
      error: error.message,
      count: 0,
      minMs: 0,
      maxMs: 0,
      meanMs: 0,
      p50Ms: 0,
      p95Ms: 0,
      p99Ms: 0,
      reconnects: 0
    })
  });

  // Capture environment after the heavy work so the public IP/geo lookup happens last.
  onPhaseChange({ phase: 'capture-env' });
  const environment = await runWithTimeBudget({
    label: 'environment capture',
    timeoutMs: 10_000,
    run: () => captureEnvironment({ serverUrl: serverHttpUrl }).catch(() => ({})),
    fallback: error => ({ capture_error: error.message })
  });

  const metrics = deriveNetworkMetrics({ webrtc, bufferbloat, ws, bandwidth: { download: dl, upload: up, capacityDownload, capacityUpload } });
  const evaluated = evaluateAll(metrics);
  const networkOverall = networkReliabilityResult(evaluated.verdicts);
  const overall = overallResult({
    machine: machine.machineVerdict,
    network: networkOverall
  });

  const completedAt = new Date().toISOString();

  return {
    started_at: startedAt,
    completed_at: completedAt,
    overall_result: overall,
    network_result: networkOverall,
    machine,
    metrics: evaluated.metrics,
    verdicts: evaluated.verdicts,
    environment,
    raw_metrics: {
      machine: machine.raw,
      via_cloudflare_tunnel: environment.via_cloudflare_tunnel || false,
      idle_pings: idlePings,
      bandwidth: { download: dl, upload: up, capacityDownload, capacityUpload },
      bufferbloat,
      websocket: ws,
      webrtc
    }
  };
}
